import { useMemo } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import { Avatar, Empty, ErrorState, Legend, Loading, PageHead, Section } from '../components/common';
import { useSchedule, useScores } from '../lib/api';
import { fmtPoints } from '../lib/format';
import { buildTeamViews, sortStandings, teamIndex } from '../lib/league';
import { buildStrength } from '../lib/odds';
import { Link } from '../lib/router';
import {
  buildFixtures,
  meetingCounts,
  meetingsFor,
  scheduleStrength,
  scheduleWeeks,
  type Fixture,
} from '../lib/schedule';

/**
 * One cell of the season grid: who you play that week, and how it went.
 *
 * The opponent is an avatar rather than a name because the grid is fourteen
 * columns wide before it starts. A name column that fits the longest team name
 * makes the table 1,900px and unreadable on anything; the avatar is already the
 * identifier every other page in the site uses. The accessible name and the
 * score ride along in `title` and in visually-hidden text so the grid is not
 * image-only.
 */
function GridCell({
  fixture,
  opponentName,
  opponentAvatar,
  season,
}: {
  fixture: Fixture | undefined;
  opponentName: string;
  opponentAvatar: string | null;
  season: string;
}) {
  if (!fixture) return <td className="sched-cell sched-bye">—</td>;

  const played = fixture.outcome !== null;
  const label = played
    ? `${fixture.outcome} ${fmtPoints(fixture.points ?? 0)}-${fmtPoints(fixture.opponentPoints ?? 0)} vs ${opponentName}`
    : `Week ${fixture.week} vs ${opponentName}`;

  const inner = (
    <>
      <Avatar id={opponentAvatar} alt={opponentName} />
      <span className="sr-only">{label}</span>
      {played && <span className={`sched-result sched-${fixture.outcome}`}>{fixture.outcome}</span>}
    </>
  );

  return (
    <td className={`sched-cell${played ? ` sched-played sched-cell-${fixture.outcome}` : ''}`} title={label}>
      {played ? (
        <Link to={`/recap/${season}/${fixture.week}`} className="sched-link">
          {inner}
        </Link>
      ) : (
        <span className="sched-link">{inner}</span>
      )}
    </td>
  );
}

export default function Schedule({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  const { data, error, loading, reload } = useSchedule(bundle.league.league_id);

  // Completed seasons feed the preseason strength prior, exactly as on the odds
  // page. Optional: without them every team simply sits at the league average
  // and the opponent columns still render, just undifferentiated.
  const priorLeagueIds = useMemo(
    () =>
      (history?.seasons ?? [])
        .filter((s) => s.season !== bundle.league.season)
        .sort((a, b) => Number(a.season) - Number(b.season))
        .map((s) => s.league_id),
    [history, bundle.league.season],
  );
  const priors = useScores(priorLeagueIds);

  const teams = useMemo(() => buildTeamViews(bundle), [bundle]);
  const byRoster = useMemo(() => teamIndex(teams), [teams]);
  const ordered = useMemo(() => sortStandings(teams), [teams]);

  const weeks = useMemo(() => scheduleWeeks(data), [data]);
  const fixtures = useMemo(() => buildFixtures(data), [data]);
  const counts = useMemo(() => meetingCounts(data), [data]);

  // Opponent quality measured as expected points per week rather than as
  // scoring so far, so the column says something before week one.
  const model = useMemo(() => buildStrength(data, priors.data ?? []), [data, priors.data]);
  const levels = useMemo(
    () => new Map([...model.teams].map(([id, t]) => [id, t.strength as number | null])),
    [model],
  );
  const strength = useMemo(() => scheduleStrength(data, levels), [data, levels]);

  if (loading) return <Loading label="Loading the fixture list" />;
  if (error || !data) return <ErrorState message={error ?? 'No schedule returned'} onRetry={reload} />;

  if (data.games.length === 0) {
    return (
      <>
        <PageHead title="Schedule" meta={[`${data.season} season`]} />
        <div className="card">
          <Empty>Sleeper has not generated a schedule for this season yet.</Empty>
        </div>
      </>
    );
  }

  const rosterIds = ordered.map((t) => t.roster_id);
  const playedGames = data.games.filter((g) => g.pointsA !== null).length;
  const name = (id: number) => byRoster.get(id)?.name ?? `Roster ${id}`;

  return (
    <>
      <PageHead
        title="Schedule"
        meta={[
          `${data.season} regular season`,
          `${data.regularSeasonWeeks} weeks`,
          `${data.games.length} games`,
          playedGames > 0 && `${playedGames} played`,
        ]}
      />

      <Section
        title="Season grid"
        hint={playedGames > 0 ? 'Rows in standings order' : undefined}
      >
        <div className="card table-scroll">
          {/* `sched-live` gates the dimming of upcoming fixtures. Before a ball
              is kicked EVERY cell is upcoming, and dimming the lot makes a
              perfectly good grid look like a rendering fault rather than a
              schedule. The contrast only means something once there is
              something to contrast against. */}
          <table className={`sched-grid${playedGames > 0 ? ' sched-live' : ''}`}>
            <thead>
              <tr>
                <th>Team</th>
                {weeks.map((w) => (
                  <th key={w} className="sched-week">
                    {w}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.map((team) => {
                const row = fixtures.get(team.roster_id);
                const byWeek = new Map((row?.fixtures ?? []).map((f) => [f.week, f]));
                return (
                  <tr key={team.roster_id}>
                    <td>
                      <Link to={`/teams/${team.roster_id}`}>
                        <div className="team-cell">
                          <Avatar id={team.avatar} alt={team.name} />
                          <div className="team-names">
                            <div className="team-title">{team.name}</div>
                          </div>
                        </div>
                      </Link>
                    </td>
                    {weeks.map((w) => {
                      const fixture = byWeek.get(w);
                      const opp = fixture ? byRoster.get(fixture.opponent) : undefined;
                      return (
                        <GridCell
                          key={w}
                          fixture={fixture}
                          opponentName={opp?.name ?? 'Unknown'}
                          opponentAvatar={opp?.avatar ?? null}
                          season={data.season}
                        />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Strength of schedule">
        <div className="card table-scroll">
          <table className="picks-table">
            <thead>
              <tr>
                <th>Team</th>
                {/* Before a game is played "Faced" is empty for everyone and
                    "To come" is identical to "Full season", so the split would
                    be one real column dressed up as three. */}
                {playedGames > 0 && <th className="num">Faced</th>}
                {playedGames > 0 && <th className="num">To come</th>}
                <th className="num">Full season</th>
                <th className="num">vs league</th>
                <th>Plays twice</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((team) => {
                const s = strength.get(team.roster_id);
                const m = meetingsFor(counts, team.roster_id, rosterIds);
                const vs = s?.vsLeague ?? null;
                return (
                  <tr key={team.roster_id}>
                    <td>
                      <Link to={`/teams/${team.roster_id}`}>
                        <div className="team-cell">
                          <Avatar id={team.avatar} alt={team.name} />
                          <div className="team-names">
                            <div className="team-title">{team.name}</div>
                          </div>
                        </div>
                      </Link>
                    </td>
                    {playedGames > 0 && (
                      <td className="num tabnum faint">
                        {s?.playedOpp != null ? fmtPoints(s.playedOpp) : '—'}
                      </td>
                    )}
                    {playedGames > 0 && (
                      <td className="num tabnum">
                        {s?.remainingOpp != null ? fmtPoints(s.remainingOpp) : '—'}
                      </td>
                    )}
                    <td className="num tabnum">{s?.fullOpp != null ? fmtPoints(s.fullOpp) : '—'}</td>
                    <td className="num">
                      {vs === null || Math.abs(vs) < 0.005 ? (
                        <span className="faint tabnum">—</span>
                      ) : (
                        // Positive is a HARDER draw, so the colouring is
                        // inverted against the usual convention: a tough
                        // schedule is bad news for the team it belongs to.
                        <span className={`tabnum ${vs > 0 ? 'bad' : 'good'}`}>
                          {vs > 0 ? '+' : '−'}
                          {Math.abs(vs).toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="sched-faces">
                        {m.twice.map((id) => (
                          <span key={id} className="sched-face" title={name(id)}>
                            <Avatar id={byRoster.get(id)?.avatar ?? null} alt={name(id)} />
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            {
              term: 'Full season',
              def: 'expected points per week of every opponent on the card',
            },
            playedGames > 0 && {
              term: 'Faced',
              def: 'expected points per week of opponents already played',
            },
            playedGames > 0 && {
              term: 'To come',
              def: 'expected points per week of opponents still on the card',
            },
            { term: 'vs league', def: 'full-season opponent level against the league average' },
            {
              term: 'Plays twice',
              def: `the ${
                meetingsFor(counts, rosterIds[0], rosterIds).twice.length
              } opponents met twice; the rest are met once`,
            },
          ]}
        />
      </Section>
    </>
  );
}
